import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FilePlus2, Printer, Trash2, Search, FileText } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate, pad } from "@/lib/api";
import { TEMPLATES } from "@/lib/suratTemplates";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/StatCard";
import { FieldSelect } from "@/components/FieldSelect";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function LayananSurat() {
  const { isAdmin } = useAuth();
  const nav = useNavigate();
  const [list, setList] = useState([]);
  const [jenis, setJenis] = useState("");
  const [search, setSearch] = useState("");

  const load = useCallback(() => {
    api.get("/surat", { params: { ...(jenis && { jenis }), ...(search && { search }) } }).then((r) => setList(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [jenis, search]);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const remove = async (s) => {
    if (!window.confirm(`Hapus arsip surat ${s.nomor}?`)) return;
    try {
      await api.delete(`/surat/${s.id}`);
      toast.success("Arsip surat dihapus");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div data-testid="layanan-surat-page">
      <PageHeader kicker={`${TEMPLATES.length} jenis surat keterangan`} title="Layanan Surat">
        {isAdmin && (
          <Button asChild className="bg-[#0b4f4a] hover:bg-[#072f2c]" data-testid="surat-new-button">
            <Link to="/surat/baru"><FilePlus2 className="h-4 w-4 mr-2" /> Buat Surat</Link>
          </Button>
        )}
      </PageHeader>

      {isAdmin && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
          {TEMPLATES.map((t) => (
            <button key={t.id} onClick={() => nav(`/surat/baru?jenis=${t.id}`)} data-testid={`surat-tpl-${t.id}`}
              className="group text-left bg-white border rounded-xl p-4 transition-colors duration-200 hover:border-[#0b4f4a] hover:bg-emerald-50/40">
              <FileText className="h-5 w-5 text-amber-600 transition-transform duration-200 group-hover:-translate-y-0.5" />
              <div className="text-sm font-semibold text-slate-900 mt-2 leading-snug">{t.judul.replace("Surat Keterangan ", "Ket. ")}</div>
              <div className="text-[11px] font-mono text-slate-500 mt-1">{t.pola.replace("{n}", "…").replace("{y}", "2026")}</div>
            </button>
          ))}
        </div>
      )}

      <h3 className="font-semibold text-slate-900 mb-3">Arsip Surat Tersimpan</h3>
      <div className="bg-white border rounded-xl p-3 grid sm:grid-cols-[1fr_320px] gap-2 mb-3">
        <div className="relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari nama, NIK, atau nomor surat" className="pl-9" data-testid="surat-search-input" />
        </div>
        <FieldSelect value={jenis} onChange={setJenis} options={TEMPLATES.map((t) => ({ value: t.id, label: t.judul }))} allLabel="Semua jenis surat" testid="surat-filter-jenis" />
      </div>
      <div className="bg-white border rounded-xl overflow-x-auto">
        <table className="w-full text-sm" data-testid="surat-table">
          <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-500">
            <tr>{["Tanggal", "Nomor", "Jenis Surat", "Nama", "NIK", "RT", ""].map((h) => <th key={h} className="px-4 py-3 whitespace-nowrap">{h}</th>)}</tr>
          </thead>
          <tbody>
            {list.map((s) => (
              <tr key={s.id} className="border-t hover:bg-amber-50/40" data-testid={`surat-row-${s.id}`}>
                <td className="px-4 py-3 whitespace-nowrap">{fmtDate(s.tanggal)}</td>
                <td className="px-4 py-3 font-mono text-xs whitespace-nowrap">{s.nomor}</td>
                <td className="px-4 py-3">{s.judul}</td>
                <td className="px-4 py-3 font-semibold whitespace-nowrap">{s.nama}</td>
                <td className="px-4 py-3 font-mono text-xs">{s.nik}</td>
                <td className="px-4 py-3">{pad(s.rt)}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-1 justify-end">
                    <Link to={`/surat/${s.id}/cetak`} className="h-8 w-8 grid place-items-center rounded-lg border hover:bg-slate-100" data-testid={`surat-print-${s.id}`}><Printer className="h-4 w-4" /></Link>
                    {isAdmin && <button onClick={() => remove(s)} className="h-8 w-8 grid place-items-center rounded-lg border hover:bg-rose-50 hover:text-rose-600" data-testid={`surat-delete-${s.id}`}><Trash2 className="h-4 w-4" /></button>}
                  </div>
                </td>
              </tr>
            ))}
            {list.length === 0 && <tr><td colSpan={7} className="px-4 py-14 text-center text-slate-500" data-testid="surat-empty">Belum ada surat tersimpan.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
